import { SearchX, Users } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Skeleton, SkeletonRegion } from '@/components/ui/skeleton';
import { problemMessage } from '../api/problem';
import { StateMessage } from '../ui/StateMessage';
import { PeopleListItem, type Actions } from './PeopleRows';
import { itemKey, type PeopleItem, type usePeople } from './use-people';

type ListState = ReturnType<typeof usePeople>['state'];

function ListSkeleton() {
  const { t } = useTranslation('administration');
  return (
    <SkeletonRegion label={t('common.loading')} className="flex flex-col gap-2">
      {[0, 1, 2, 3, 4].map((n) => (
        <Skeleton key={n} className="h-14 w-full" />
      ))}
    </SkeletonRegion>
  );
}

function LoadError({
  state,
  reload,
}: {
  readonly state: Extract<ListState, { status: 'error' }>;
  readonly reload: () => void;
}) {
  const { t } = useTranslation('administration');
  return (
    <StateMessage
      role="alert"
      tone="error"
      icon={Users}
      title={t('people.loadError')}
      text={problemMessage(t, state.code, state.httpStatus)}
      action={
        <Button variant="outline" onClick={reload}>
          {t('common.retry')}
        </Button>
      }
    />
  );
}

function EmptyState({
  filtered,
  placeName,
  onClear,
}: {
  readonly filtered: boolean;
  readonly placeName: string;
  readonly onClear: () => void;
}) {
  const { t } = useTranslation('administration');
  if (filtered) {
    return (
      <StateMessage
        icon={SearchX}
        title={t('people.noResults.title')}
        text={t('people.noResults.text')}
        action={
          <Button variant="outline" onClick={onClear}>
            {t('people.noResults.clear')}
          </Button>
        }
      />
    );
  }
  return (
    <StateMessage
      icon={Users}
      title={t('people.firstUse.title', { place: placeName })}
      text={t('people.firstUse.text')}
    />
  );
}

function ListFoot({
  state,
  more,
  reload,
}: {
  readonly state: ListState;
  readonly more: () => void;
  readonly reload: () => void;
}) {
  const { t } = useTranslation('administration');
  return (
    <div className="people-list-foot">
      <span>{state.status === 'ready' ? t('people.results', { count: state.total }) : null}</span>
      {state.status === 'ready' && state.hasMore ? (
        <Button variant="outline" size="sm" onClick={more} disabled={state.loadingMore}>
          {state.loadingMore ? t('common.loading') : t('common.more')}
        </Button>
      ) : null}
      {state.status === 'error' ? (
        <span role="alert" className="flex items-center gap-2">
          {problemMessage(t, state.code, state.httpStatus)}
          <Button variant="outline" size="sm" onClick={reload}>
            {t('common.retry')}
          </Button>
        </span>
      ) : null}
    </div>
  );
}

/** The people and invitations list, or the loading, error or empty state that stands in for it. */
export function PeopleListPane({
  state,
  items,
  more,
  reload,
  filtered,
  placeName,
  actions,
  onClear,
  personHref,
  onHide,
  onRestore,
}: {
  readonly state: ListState;
  readonly items: readonly PeopleItem[];
  readonly more: () => void;
  readonly reload: () => void;
  readonly filtered: boolean;
  readonly placeName: string;
  readonly actions: Actions;
  readonly onClear: () => void;
  readonly personHref: (id: string) => string;
  readonly onHide: (key: string) => void;
  readonly onRestore: (key: string) => void;
}) {
  const { t } = useTranslation('administration');
  if (state.status === 'loading' && state.items.length === 0) return <ListSkeleton />;
  if (state.status === 'error' && state.items.length === 0) {
    return <LoadError state={state} reload={reload} />;
  }
  if (items.length === 0) {
    return <EmptyState filtered={filtered} placeName={placeName} onClear={onClear} />;
  }
  return (
    <>
      <div
        className="people-list"
        role="list"
        aria-label={t('people.listLabel', { place: placeName })}
      >
        <div className="people-list-head" aria-hidden="true">
          <span>{t('people.columns.person')}</span>
          <span>{t('people.columns.roles')}</span>
          <span>{t('people.columns.status')}</span>
          <span>{t('people.columns.updated')}</span>
          <span />
        </div>
        {items.map((item) => (
          <PeopleListItem
            key={itemKey(item)}
            item={item}
            placeName={placeName}
            personHref={personHref}
            actions={actions}
            onHide={() => onHide(itemKey(item))}
            onRestore={() => onRestore(itemKey(item))}
          />
        ))}
      </div>
      <ListFoot state={state} more={more} reload={reload} />
    </>
  );
}
